import { getFileExtension, isVideo } from '@/utils/helpers';
import { useHead } from '#imports';
import useImageParams from '@/composables/useImageParams';
import type { RouteLocationNormalizedLoadedGeneric } from '#vue-router';

type UseMetaCollectionSlugIdProps =
    Pick<AppWTFNft, 'name' | 'description' | 'displayAnimationURL' | 'displayImageURL' | 'metadata'>
    & { route: RouteLocationNormalizedLoadedGeneric };

export default function useMetaCollectionSlugId({
    name,
    description,
    displayAnimationURL,
    displayImageURL,
    route,
    metadata
}: UseMetaCollectionSlugIdProps) {
    const contentWidth = 1200;
    const contentHeight = 1200;

    const defaultImg = '/og_1200x630.jpg';
    const defaultExt = 'image/jpg';

    const contentUrl = displayAnimationURL && isVideo(displayAnimationURL)
        ? displayAnimationURL
        : !displayImageURL
                ? defaultImg
                : useImageParams(
                        displayImageURL,
                        contentWidth
                    );

    const pathWithoutParams = import.meta.env.VITE_DOMEN_URL + route.path;
    const pathWithParams = import.meta.env.VITE_DOMEN_URL + route.fullPath;

    const videoTags = [
        {
            property: 'og:video:type',
            content: `video/${getFileExtension(contentUrl)}`
        },
        {
            property: 'og:video:width',
            content: `${contentWidth}`
        },
        {
            property: 'og:video:height',
            content: `${contentHeight}`
        },
        {
            property: 'og:video',
            content: contentUrl
        },
        {
            property: 'twitter:card',
            content: 'player'
        },
        {
            property: 'twitter:player',
            content: pathWithoutParams
        },
        {
            property: 'twitter:player:stream:content_type',
            content: `video/${getFileExtension(contentUrl)}`
        },
        {
            property: 'twitter:player:width',
            content: `${contentWidth}`
        },
        {
            property: 'twitter:player:height',
            content: `${contentHeight}`
        },
        {
            name: 'twitter:image',
            content: displayImageURL
        }
    ];

    useHead({
        title: name,
        meta: [
            {
                name: 'description',
                content: description
            },
            {
                name: 'keywords',
                content:
                    `${name.toLowerCase() || ''} background image, icon, collection name, collection Items, collection owners, floor price, 1D Volume`
            },
            {
                property: 'og:image:type',
                content: contentUrl === defaultImg
                    ? defaultExt
                    : `image/${getFileExtension(contentUrl)}`
            },
            {
                property: 'og:image:width',
                content: `${contentWidth}`
            },
            {
                property: 'og:image:height',
                content: `${contentHeight}`
            },
            {
                property: 'og:image',
                content: contentUrl
            },
            {
                property: 'og:image:alt',
                content: name
            },
            {
                name: 'twitter:image',
                content: contentUrl
            },
            {
                property: 'og:title',
                content: name
            },
            {
                property: 'og:description',
                content:
                description
            },
            {
                name: 'twitter:title',
                content: name
            },
            {
                name: 'twitter:card',
                content: 'summary_large_image'
            },
            {
                name: 'twitter:url',
                content: pathWithoutParams
            },
            {
                name: 'twitter:creator',
                content: metadata
            },
            {
                name: 'twitter:description',
                content: description
            },
            ...displayAnimationURL && isVideo(displayAnimationURL)
                ? videoTags
                : []
        ],
        link: [
            {
                rel: 'canonical',
                href: pathWithParams
            },
            {
                rel: 'preconnect',
                href: pathWithParams
            },
            {
                rel: 'dns-prefetch',
                href: pathWithParams
            }
        ]
    });
}
